import axios from 'axios';
import queryString from 'query-string';

import { asyncSpan, getLogger, LogLevel } from '@oaknorth/util-rum';

import { isAxiosError } from './errors';
import { FetcherOptions, QueryStringFormat } from './types';

type Params = Record<string, unknown>;

// exporting for testing
export function paramsSerializerFormExplode(params: Params): string {
  // default arrayFormat is `repeat`
  return queryString.stringify(params);
}

// exporting for testing
export function paramsSerializerFormNoExplode(params: Params): string {
  return queryString.stringify(params, { arrayFormat: 'comma' });
}

const PARAMS_SERIALIZERS: Record<QueryStringFormat, (params: Params) => string> = {
  [QueryStringFormat.FORM_EXPLODE]: paramsSerializerFormExplode,
  [QueryStringFormat.FORM_NO_EXPLODE]: paramsSerializerFormNoExplode,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ReponseData type not used here
function getContextFromOptions(options: FetcherOptions<any>): Record<string, string> {
  return {
    url: options.url,
    // eslint-disable-next-line i18next/no-literal-string -- Not user visible
    method: options.method || 'GET',
    params: JSON.stringify(options.query),
  };
}

function fetcher<ResponseData>(options: FetcherOptions<ResponseData>): Promise<ResponseData> {
  const {
    query,
    method,
    body,
    url,
    headers,
    responseType,
    queryStringFormat = QueryStringFormat.FORM_NO_EXPLODE,
    onFulfilled,
  } = options;

  return (
    axios
      .request({
        url,
        params: query,
        paramsSerializer: {
          serialize: PARAMS_SERIALIZERS[queryStringFormat],
        },
        method,
        data: body,
        withCredentials: true,
        headers,
        responseType,
      })
      // This uses .then and .catch rather than async/await because it's required to use asyncSpan
      .then((result) => {
        return onFulfilled ? onFulfilled(result) : result.data;
      })
      .catch((error) => {
        const logger = getLogger();
        const context = getContextFromOptions(options);

        if (isAxiosError(error)) {
          context.statusCode = String(error.request.status);
        }

        logger.log(String(error), {
          level: LogLevel.ERROR,
          context,
        });

        throw error;
      })
  );
}
const tracedFetcher = asyncSpan('fetcher', fetcher, (options) => getContextFromOptions(options));
export { tracedFetcher as fetcher };
